// ============================================================================
// Facebook Page — uploadMedia capability method.
//
// Scope for #788:
//   - `kind: 'image' | 'carousel_item'`
//                       → single-shot POST `/photos?published=false` returning
//                         the photo id (used later as a `media_fbid` in
//                         `attached_media`). FB multi-image carousel posts use
//                         this same photo-id flow.
//   - `kind: 'single_video' | 'reel'` → return `unsupported_method`.
//                         These require a media-kind-aware publish contract,
//                         because they do NOT publish via `/feed.attached_media`.
//
// Two source modes supported on images:
//   - `sourceUrl`: HTTPS URL Meta will fetch the binary from. Adapter
//                  validates HTTPS, then passes via `url` param.
//   - `dataBase64`: inline payload, sent via multipart/form-data `source`
//                   field. Hard-capped at 4MB (Cloudflare Workers body
//                   limit); larger payloads must use sourceUrl.
// ============================================================================

import type {
  SocialPostingCallCtx,
  SocialResult,
  SocialUploadMediaArgs,
  SocialUploadMediaResult,
} from '../../capabilities/social-posting'
import type { RateLimitCheck } from './rate-limit-check'
import { errorFromResponse, sanitiseMetaErrorString } from './errors'
import { FB_PAGE_GRAPH_API_BASE } from './oauth'
import type { FacebookPagePublishResponse } from './types'

// ============================================================================
// Constants
// ============================================================================

const ENDPOINT_LABEL = 'facebook:page_upload_media'

/** Cloudflare Worker body limit — 4MB. Larger images must use sourceUrl. */
const MAX_INLINE_BYTES = 4 * 1024 * 1024

// ============================================================================
// uploadMedia
// ============================================================================

export interface UploadMediaDeps {
  fetchImpl?: typeof fetch
  graphApiBase?: string
  /**
   * Rate-limit check callback. When omitted, the adapter fail-opens (passes
   * every request through). The dispatcher / registration site wires a real
   * check that delegates to `services/rate-limiter.service`.
   */
  rateLimitCheck?: RateLimitCheck
}

export async function uploadMedia(
  args: SocialUploadMediaArgs,
  pageAccessToken: string,
  ctx: SocialPostingCallCtx,
  deps: UploadMediaDeps = {},
): Promise<SocialResult<SocialUploadMediaResult>> {
  if (typeof pageAccessToken !== 'string' || pageAccessToken.length === 0) {
    return {
      ok: false,
      error: {
        code: 'auth',
        message: 'Facebook Page access token is missing',
        retryable: false,
      },
    }
  }

  // --- Validate kind --------------------------------------------------
  if (args.kind !== 'image' && args.kind !== 'carousel_item') {
    // TODO: implement video/reel via a media-kind-aware publish contract. Until
    // then mark explicitly unsupported instead of uploading a media id that the
    // current `/feed` publish step cannot consume correctly.
    return {
      ok: false,
      error: {
        code: 'unsupported_method',
        message: `Facebook Page adapter supports kind:'image' and image carousel_item for uploadMedia (got ${args.kind}). Video/Reel publishing needs a media-kind-aware follow-up.`,
        retryable: false,
      },
    }
  }
  if (args.kind === 'carousel_item' && typeof args.mime === 'string' && args.mime.startsWith('video/')) {
    return {
      ok: false,
      error: {
        code: 'unsupported_method',
        message: 'Facebook Page carousel_item currently supports image media only',
        retryable: false,
      },
    }
  }

  if (typeof args.targetAccountId !== 'string' || args.targetAccountId.length === 0) {
    return {
      ok: false,
      error: {
        code: 'provider_rejected',
        message: 'targetAccountId is required',
        retryable: false,
      },
    }
  }

  const hasUrl = typeof args.sourceUrl === 'string' && args.sourceUrl.length > 0
  const hasInline = typeof args.dataBase64 === 'string' && args.dataBase64.length > 0
  if (hasUrl === hasInline) {
    return {
      ok: false,
      error: {
        code: 'provider_rejected',
        message: 'uploadMedia requires exactly one of `sourceUrl` or `dataBase64`',
        retryable: false,
      },
    }
  }

  if (hasUrl && !args.sourceUrl!.startsWith('https://')) {
    return {
      ok: false,
      error: {
        code: 'media_invalid',
        message: 'sourceUrl must be HTTPS',
        retryable: false,
      },
    }
  }

  // --- Rate-limit gate ------------------------------------------------
  const gate = await checkRateLimit(ctx, ENDPOINT_LABEL, deps.rateLimitCheck)
  if (!gate.ok) return gate as SocialResult<SocialUploadMediaResult>

  const fetchImpl = deps.fetchImpl ?? fetch
  const base = deps.graphApiBase ?? FB_PAGE_GRAPH_API_BASE
  const url = `${base}/${encodeURIComponent(args.targetAccountId)}/photos`

  let response: Response
  try {
    if (hasUrl) {
      response = await uploadByUrl(url, args, pageAccessToken, fetchImpl)
    } else {
      response = await uploadByInline(url, args, pageAccessToken, fetchImpl)
    }
  } catch (err) {
    // `uploadByInline` may throw a `SocialResult`-shaped object when the
    // payload exceeds the inline cap; we re-raise into the result.
    if (isResultLikeError(err)) return err as SocialResult<SocialUploadMediaResult>
    ctx.redactedLogger.error('uploadMedia network error', {
      error: err instanceof Error ? err.message : 'unknown',
    })
    return {
      ok: false,
      error: {
        code: 'transient',
        message: 'Network error talking to Facebook Graph',
        retryable: true,
      },
    }
  }

  if (!response.ok) {
    const err = await errorFromResponse(response, 'Facebook Graph upload failed')
    ctx.redactedLogger.warn('uploadMedia failed', {
      code: err.code,
      httpStatus: response.status,
      providerTraceId: err.providerTraceId,
    })
    return { ok: false, error: err }
  }

  const body = (await response.json().catch(() => null)) as FacebookPagePublishResponse | null
  if (!body || typeof body.id !== 'string' || body.id.length === 0) {
    return {
      ok: false,
      error: {
        code: 'unknown',
        message: 'Facebook Graph returned no photo id',
        retryable: false,
      },
    }
  }

  return {
    ok: true,
    data: {
      providerMediaId: body.id,
      status: 'ready',
      // Meta photo containers don't have a documented expiry, but unconsumed
      // unpublished photos linger for ~24h before garbage collection.
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
    },
  }
}

// ============================================================================
// Helpers
// ============================================================================

async function uploadByUrl(
  url: string,
  args: SocialUploadMediaArgs,
  pageAccessToken: string,
  fetchImpl: typeof fetch,
): Promise<Response> {
  const params = new URLSearchParams({
    url: args.sourceUrl!,
    published: 'false',
  })
  if (typeof args.caption === 'string' && args.caption.length > 0) {
    params.set('caption', args.caption)
  }
  return fetchImpl(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: `Bearer ${pageAccessToken}`,
    },
    body: params.toString(),
  })
}

async function uploadByInline(
  url: string,
  args: SocialUploadMediaArgs,
  pageAccessToken: string,
  fetchImpl: typeof fetch,
): Promise<Response> {
  const bytes = decodeBase64(args.dataBase64!)
  if (bytes.length > MAX_INLINE_BYTES) {
    throw {
      ok: false,
      error: {
        code: 'media_invalid',
        message: `Inline image exceeds Workers body limit of ${MAX_INLINE_BYTES} bytes; use sourceUrl instead.`,
        retryable: false,
      },
    } as SocialResult<never>
  }

  const mime = typeof args.mime === 'string' && args.mime.length > 0 ? args.mime : 'image/jpeg'
  const filename = typeof args.filename === 'string' && args.filename.length > 0 ? args.filename : 'upload.jpg'

  const form = new FormData()
  // Convert raw bytes -> Blob -> File (FormData expects a Blob/string).
  // `Uint8Array.buffer` is `ArrayBufferLike`; Blob accepts both, but stricter
  // TS lib targets require we slice into a plain ArrayBuffer.
  const buffer: ArrayBuffer = bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  ) as ArrayBuffer
  const blob = new Blob([buffer], { type: mime })
  form.append('source', blob, filename)
  form.append('published', 'false')
  if (typeof args.caption === 'string' && args.caption.length > 0) {
    form.append('caption', args.caption)
  }

  return fetchImpl(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${pageAccessToken}`,
      // Don't set Content-Type — fetch infers it with the multipart boundary.
    },
    body: form,
  })
}

function decodeBase64(b64: string): Uint8Array {
  // Workers + Node both expose `atob`; older Node tests may not. Use a
  // small polyfill via Buffer when atob is unavailable.
  let binary: string
  if (typeof atob === 'function') {
    binary = atob(b64)
  } else {
    const bufImpl = (globalThis as { Buffer?: { from: (s: string, enc: string) => Uint8Array } }).Buffer
    if (!bufImpl) {
      throw new Error(sanitiseMetaErrorString('No base64 decoder available in runtime'))
    }
    binary = String.fromCharCode(...bufImpl.from(b64, 'base64'))
  }
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

function isResultLikeError(err: unknown): err is SocialResult<never> {
  if (!err || typeof err !== 'object') return false
  const obj = err as Record<string, unknown>
  return obj.ok === false && typeof obj.error === 'object'
}

async function checkRateLimit(
  ctx: SocialPostingCallCtx,
  endpoint: string,
  rateLimitCheck: RateLimitCheck | undefined,
): Promise<SocialResult<true>> {
  if (!rateLimitCheck) return { ok: true, data: true }
  const decision = await rateLimitCheck({ instanceId: ctx.instanceId, endpoint }, ctx)
  if (decision.allowed) return { ok: true, data: true }
  return {
    ok: false,
    error: {
      code: 'rate_limit',
      message: 'Rate limit exceeded for facebook:page_upload_media',
      retryable: true,
      retryAfterSeconds: decision.retryAfterSeconds,
    },
  }
}
